import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KnowledgeSpace } from '@deep-agents/shared-types';
import { Sidebar } from '../../components/layout';

// Trash icon for delete button
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

export function KnowledgeSpacesPage() {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState<KnowledgeSpace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState('');
  const [type, setType] = useState<'personal' | 'team'>('personal');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingSpaceId, setDeletingSpaceId] = useState<string | null>(null);

  const fetchSpaces = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/kb/spaces', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSpaces(data?.data?.spaces || []);
      }
    } catch (err) {
      console.error('Failed to load spaces', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  const formatRelativeTime = (timestamp?: number | null) => {
    if (!timestamp) return 'Not synced yet';
    const now = Date.now();
    const diff = now - timestamp * 1000;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const handleCreateSpace = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch('/api/kb/spaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, type }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to create space');
      }

      setName('');
      setType('personal');
      await fetchSpaces();

      if (data?.data?.space?.id) {
        navigate(`/knowledge/${data.data.space.id}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create space';
      setError(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSpace = async (spaceId: string, spaceName: string, spaceType: string, event: React.MouseEvent) => {
    // Prevent navigation when clicking delete
    event.stopPropagation();

    // Board spaces cannot be deleted
    if (spaceType === 'board') {
      setError('Board spaces cannot be deleted directly. Delete the board instead.');
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Delete "${spaceName}"? This will remove all documents and notes in this space. This cannot be undone.`)) {
      return;
    }

    setDeletingSpaceId(spaceId);
    setError(null);

    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to delete space');
      }

      // Refresh the list
      await fetchSpaces();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete space';
      setError(message);
    } finally {
      setDeletingSpaceId(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-color-neutral-2 font-inter text-color-main">
      <Sidebar />

      <main className="flex-1 ml-[240px]">
        <header className="flex h-16 items-center justify-between border-b border-color-neutral-3 bg-white px-6">
          <h1 className="text-lg font-semibold text-color-main">Knowledge Spaces</h1>
        </header>

        <div className="p-8">
          <div className="mb-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-color-neutral-3 bg-white p-6 shadow-card-light">
              <div className="mb-4 text-sm font-semibold text-color-main">Create a new space</div>
              <div className="flex flex-col gap-4">
                <input
                  type="text"
                  className="rounded-xl border border-color-neutral-3 bg-color-neutral-1 px-4 py-2 text-sm"
                  placeholder="Space name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
                <div className="flex gap-3">
                  {(['personal', 'team'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setType(option)}
                      className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                        type === option
                          ? 'bg-color-main text-white'
                          : 'bg-color-neutral-1 text-color-blended'
                      }`}
                    >
                      {option === 'personal' ? 'Personal' : 'Team'}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="rounded-xl bg-color-main px-4 py-2 text-sm font-medium text-white transition hover:bg-color-main-darker"
                  onClick={handleCreateSpace}
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create space'}
                </button>
                {error && <div className="text-xs text-red-500">{error}</div>}
              </div>
            </div>

            <div className="rounded-2xl border border-color-neutral-3 bg-white p-6 shadow-card-light">
              <div className="text-sm font-semibold text-color-main">How spaces work</div>
              <p className="mt-3 text-sm text-color-blended leading-relaxed">
                Spaces store documents, URLs, and notes that power retrieval for boards and agents.
                Every board has its own dedicated space, and you can create team or personal spaces
                to share knowledge across projects.
              </p>
              <div className="mt-4 rounded-xl bg-color-neutral-1 px-4 py-3 text-xs text-color-blended">
                Tip: Use dedicated spaces to keep sources focused and improve answer quality.
              </div>
            </div>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-color-main">Your spaces</h2>
            <span className="text-xs text-color-blended">{spaces.length} total</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 rounded-2xl bg-color-neutral-3 animate-pulse" />
              ))}
            </div>
          ) : spaces.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-color-neutral-3 bg-white p-10 text-center text-sm text-color-blended">
              No spaces yet. Create one to start indexing knowledge.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {spaces.map((space) => (
                <button
                  key={space.id}
                  type="button"
                  onClick={() => navigate(`/knowledge/${space.id}`)}
                  className="group flex flex-col gap-3 rounded-2xl border border-color-neutral-3 bg-white p-5 text-left shadow-card-light transition hover:-translate-y-0.5 hover:border-color-dark-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-color-main">{space.name}</span>
                    <div className="flex items-center gap-2">
                      {/* Delete button - only for personal/team spaces, hidden for board spaces */}
                      {space.type !== 'board' && (
                        <button
                          type="button"
                          onClick={(e) => handleDeleteSpace(space.id, space.name, space.type, e)}
                          disabled={deletingSpaceId === space.id}
                          className="rounded-lg p-1.5 text-gray-400 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 disabled:opacity-50"
                          title="Delete space"
                        >
                          {deletingSpaceId === space.id ? (
                            <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-red-500" />
                          ) : (
                            <TrashIcon />
                          )}
                        </button>
                      )}
                      <span className="rounded-full bg-color-neutral-1 px-3 py-1 text-[11px] font-medium text-color-blended">
                        {space.type.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-color-blended">
                    {space.documentCount ?? 0} docs • last sync {formatRelativeTime(space.lastSyncAt)}
                  </div>
                  <div className="text-xs text-color-blended">
                    Updated {formatRelativeTime(space.updatedAt)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
